import React, { useState, useEffect, useCallback } from 'react';
import { slaAPI, slaCalendarsAPI, slaEscalationAPI, slaPauseAPI, slaPriorityAPI, masterAPI, groupsAPI, usersAPI } from '../../services/api';

const TABS = ['Policies', 'Business Calendars', 'Escalation Rules', 'Priority Rules', 'Pause Rules'];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const ROLES = ['agent', 'team_lead', 'manager', 'director', 'requester'];
const NOTIFY_ROLES = ['agent', 'team_lead', 'manager', 'director'];
const PRIORITY_COLORS = { Low: '#10b981', Medium: '#f59e0b', High: '#ef4444', Urgent: '#7c3aed' };

// ── SLA condition builder constants ──────────────────────────────────────────
const SLA_CONDITION_FIELDS = [
  'Category', 'Sub-Category', 'Tags', 'Subject', 'Type', 'Status', 'Priority',
  'Group', 'Agent', 'Department', 'Resolution Note', 'Workspace',
];
const SLA_OPERATORS_TEXT   = ['is', 'is not', 'contains', 'does not contain', 'starts with', 'ends with'];
const SLA_OPERATORS_SELECT = ['is', 'is not'];
const SLA_SELECT_VALUES = {
  Type:   ['Incident', 'Service Request', 'Change', 'Problem'],
  Status: ['Open', 'Pending Action', 'Pending Approval', 'Resolved', 'Closed'],
};
const SLA_TEXT_FIELDS = new Set(['Tags', 'Subject', 'Resolution Note', 'Workspace']);
const SLA_DROPDOWN_FIELDS = new Set([
  'Category', 'Sub-Category', 'Type', 'Status', 'Group', 'Agent', 'Department', 'Priority',
]);

function masterValues(masterData, key) {
  return (masterData?.[key] || []).map(d => d.value).filter(Boolean);
}

function uniqSorted(arr) {
  return [...new Set(arr)].sort((a, b) => String(a).localeCompare(String(b)));
}

function buildSlaConditionSelectValues({ masterData, departments, agentGroups, agents }) {
  const fromMaster = (key) => uniqSorted(masterValues(masterData, key));
  return {
    Category: fromMaster('category'),
    'Sub-Category': fromMaster('sub_category'),
    Department: uniqSorted(departments),
    Group: uniqSorted(agentGroups.map(g => g.name)),
    Agent: uniqSorted(agents),
    Priority: uniqSorted([...PRIORITIES, ...fromMaster('priority')]),
    Type: uniqSorted([...(SLA_SELECT_VALUES.Type || []), ...fromMaster('type')]),
    Status: uniqSorted([...(SLA_SELECT_VALUES.Status || []), ...fromMaster('status')]),
  };
}

function slaConditionOps(field) {
  return SLA_TEXT_FIELDS.has(field) ? SLA_OPERATORS_TEXT : SLA_OPERATORS_SELECT;
}

function parseAppliesToTarget(policy, conditions) {
  if (policy.applies_to_target) return policy.applies_to_target;
  const mode = policy.applies_to || 'All Tickets';
  if (mode === 'Department-Based') {
    return conditions.find(c => c.field === 'Department')?.value || '';
  }
  if (mode === 'Group-Based') {
    return conditions.find(c => c.field === 'Group')?.value || '';
  }
  if (mode === 'Priority-Based') {
    return conditions.find(c => c.field === 'Priority')?.value || '';
  }
  return '';
}

function buildConditionsForSave(form) {
  let conditions = [...(form.conditions || [])];
  const upsert = (field, value) => {
    if (!value) {
      return conditions.filter(c => c.field !== field);
    }
    const idx = conditions.findIndex(c => c.field === field);
    const row = { id: Date.now() + Math.random(), field, operator: 'is', value };
    if (idx >= 0) conditions[idx] = { ...conditions[idx], ...row };
    else conditions.push(row);
    return conditions;
  };
  if (form.applies_to === 'Department-Based') {
    conditions = upsert('Department', form.applies_to_target || '');
  } else if (form.applies_to === 'Group-Based') {
    conditions = upsert('Group', form.applies_to_target || '');
  } else if (form.applies_to === 'Priority-Based') {
    conditions = upsert('Priority', form.applies_to_target || '');
  }
  return conditions;
}

const s = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  scroll: { flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 2, paddingBottom: 8 },
  wrap: { padding: '0 0 32px 0' },
  tabs: { display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e5e7eb', paddingBottom: 0 },
  tab: (active) => ({
    padding: '8px 18px', cursor: 'pointer', borderRadius: '6px 6px 0 0', fontWeight: 500,
    fontSize: 14, background: active ? '#fff' : 'transparent',
    color: active ? '#111' : '#6b7280',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    transition: 'all .15s',
  }),
  card: { background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 },
  row: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  label: { fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 500 },
  input: { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%', outline: 'none' },
  select: { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff', outline: 'none' },
  btn: (c = 'var(--accent)') => ({ padding: '7px 16px', background: c, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }),
  btnGhost: { padding: '7px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: 600, fontSize: 12, color: '#374151', background: '#f9fafb' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
  badge: (c = '#e5e7eb', tc = '#374151') => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: c, color: tc, fontSize: 11, fontWeight: 600 }),
  section: { marginBottom: 24 },
  sectionTitle: { fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#111' },
  toggle: (on) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 20, borderRadius: 10, background: on ? '#2563eb' : '#d1d5db', cursor: 'pointer', transition: 'background .2s', position: 'relative' }),
  toggleKnob: (on) => ({ width: 16, height: 16, borderRadius: 8, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)', position: 'absolute', left: on ? 18 : 2, transition: 'left .2s' }),
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  errorBox: { padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 },
  successBox: { padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, color: '#166534', fontSize: 13, marginBottom: 12 },
};

function SlaConditionValueInput({ field, value, options, onChange }) {
  if (!SLA_DROPDOWN_FIELDS.has(field)) {
    return (
      <input
        style={{ ...s.input, flex: 1 }}
        placeholder={`Enter ${field.toLowerCase()}…`}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  const list = options || [];
  return (
    <select style={{ ...s.select, flex: 1 }} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— Select {field.toLowerCase()} —</option>
      {list.length === 0 ? (
        <option value="" disabled>No options — add in Master Data / Users</option>
      ) : (
        list.map(v => <option key={v} value={v}>{v}</option>)
      )}
      {value && !list.includes(value) && (
        <option value={value}>{value} (saved)</option>
      )}
    </select>
  );
}

function Toggle({ on, onChange }) {
  return (
    <div style={s.toggle(on)} onClick={() => onChange(!on)}>
      <div style={s.toggleKnob(on)} />
    </div>
  );
}

function PriorityBadge({ p }) {
  const c = PRIORITY_COLORS[p] || '#6b7280';
  return <span style={s.badge(c + '20', c)}>{p}</span>;
}

/* ══════════════════════════════════════════════════════════════
   POLICIES TAB
══════════════════════════════════════════════════════════════ */
function PoliciesTab({ calendars }) {
  const [policies, setPolicies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [masterData, setMasterData] = useState({});
  const [departments, setDepartments] = useState([]);
  const [agentGroups, setAgentGroups] = useState([]);
  const [agents, setAgents] = useState([]);

  const load = useCallback(async () => {
    try { setPolicies(await slaAPI.list()); } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    masterAPI.all()
      .then(data => {
        setMasterData(data || {});
        setDepartments((data?.department || []).map(d => d.value).filter(Boolean));
      })
      .catch(() => {});
    groupsAPI.list()
      .then(data => setAgentGroups((data || []).map(g => ({ id: g.id, name: g.name }))))
      .catch(() => {});
    usersAPI.list()
      .then(data => {
        const names = (data || [])
          .filter(u => u.role && u.role !== 'requester')
          .map(u => u.name)
          .filter(Boolean);
        setAgents(names);
      })
      .catch(() => {});
  }, []);

  const conditionSelectValues = buildSlaConditionSelectValues({
    masterData, departments, agentGroups, agents,
  });

  const openNew = () => {
    setForm({
      name: '', description: '', is_active: true, is_default: false,
      business_calendar_id: '', time_unit: 'hours', applies_to: 'All Tickets', applies_to_target: '',
      match_type: 'all', conditions: [],
      low_response_hours: 24, medium_response_hours: 8,
      high_response_hours: 4, urgent_response_hours: 1,
      low_resolution_hours: 72, medium_resolution_hours: 24,
      high_resolution_hours: 8, urgent_resolution_hours: 4,
    });
    setEditing('new');
  };

  const openEdit = (p) => {
    let conditions = [];
    try { conditions = JSON.parse(p.conditions_json || '[]'); } catch (_) {}
    setForm({
      ...p,
      business_calendar_id: p.business_calendar_id || '',
      match_type: p.match_type || 'all',
      applies_to: p.applies_to || 'All Tickets',
      applies_to_target: parseAppliesToTarget(p, conditions),
      conditions,
    });
    setEditing(p.id);
  };

  const save = async () => {
    try {
      if (['Department-Based', 'Group-Based', 'Priority-Based'].includes(form.applies_to) && !form.applies_to_target) {
        setMsg({ type: 'error', text: `Please select a ${form.applies_to === 'Group-Based' ? 'group' : form.applies_to === 'Department-Based' ? 'department' : 'priority'}.` });
        return;
      }
      const conditions = buildConditionsForSave(form);
      const data = {
        ...form,
        business_calendar_id: form.business_calendar_id || null,
        match_type: form.match_type || 'all',
        applies_to: form.applies_to || 'All Tickets',
        applies_to_target: form.applies_to_target || null,
        conditions_json: JSON.stringify(conditions),
      };
      delete data.conditions;
      if (editing === 'new') {
        await slaAPI.create(data);
      } else {
        await slaAPI.update(editing, data);
      }
      setEditing(null);
      setMsg({ type: 'success', text: 'Policy saved.' });
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this SLA policy?')) return;
    try { await slaAPI.delete(id); await load(); } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  if (loading) return <div style={{ color: '#6b7280', padding: 24 }}>Loading…</div>;

  if (editing) {
    const unit = form.time_unit || 'hours';
    return (
      <div>
        <div style={{ ...s.row, marginBottom: 16 }}>
          <button style={s.btnGhost} onClick={() => setEditing(null)}>← Back</button>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{editing === 'new' ? 'New SLA Policy' : `Edit: ${form.name}`}</h3>
        </div>
        {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
        <div style={s.card}>
          <div style={{ ...s.formGrid, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            <div style={s.formGroup}>
              <label style={s.label}>Policy Name *</label>
              <input style={s.input} value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Applies To</label>
              <select
                style={s.select}
                value={form.applies_to || 'All Tickets'}
                onChange={e => setForm(f => ({ ...f, applies_to: e.target.value, applies_to_target: '' }))}
              >
                {['All Tickets', 'Priority-Based', 'Department-Based', 'Group-Based'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            {form.applies_to === 'Department-Based' && (
              <div style={s.formGroup}>
                <label style={s.label}>Department *</label>
                <select
                  style={s.select}
                  value={form.applies_to_target || ''}
                  onChange={e => setForm(f => ({ ...f, applies_to_target: e.target.value }))}
                >
                  <option value="">— Select department —</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}
            {form.applies_to === 'Group-Based' && (
              <div style={s.formGroup}>
                <label style={s.label}>Agent Group *</label>
                <select
                  style={s.select}
                  value={form.applies_to_target || ''}
                  onChange={e => setForm(f => ({ ...f, applies_to_target: e.target.value }))}
                >
                  <option value="">— Select agent group —</option>
                  {agentGroups.map(g => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}
            {form.applies_to === 'Priority-Based' && (
              <div style={s.formGroup}>
                <label style={s.label}>Priority *</label>
                <select
                  style={s.select}
                  value={form.applies_to_target || ''}
                  onChange={e => setForm(f => ({ ...f, applies_to_target: e.target.value }))}
                >
                  <option value="">— Select priority —</option>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
            <div style={s.formGroup}>
              <label style={s.label}>Business Calendar</label>
              <select style={s.select} value={form.business_calendar_id || ''} onChange={e => setForm(f => ({ ...f, business_calendar_id: e.target.value || null }))}>
                <option value="">— None (wall clock) —</option>
                {(calendars || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Time Unit</label>
              <select style={s.select} value={form.time_unit || 'hours'} onChange={e => setForm(f => ({ ...f, time_unit: e.target.value }))}>
                {['minutes', 'hours', 'days'].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div style={{ ...s.formGroup, gridColumn: '1/-1' }}>
              <label style={s.label}>Description</label>
              <input style={s.input} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Active</label>
              <Toggle on={!!form.is_active} onChange={v => setForm(f => ({ ...f, is_active: v }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Default Policy</label>
              <Toggle on={!!form.is_default} onChange={v => setForm(f => ({ ...f, is_default: v }))} />
            </div>
          </div>

          {/* ── Apply this to: conditions builder ─────────────────────────── */}
          <div style={{ margin: '20px 0 0', borderTop: '1px solid #e5e7eb', paddingTop: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Apply this to</div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>Choose when this SLA policy must be enforced</div>
            {/* Match ALL / ANY */}
            <div style={{ display: 'flex', gap: 28, marginBottom: 14, flexWrap: 'wrap' }}>
              {[['all', 'ALL'], ['any', 'ANY']].map(([val, label]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="radio"
                    name="sla_match_type"
                    checked={(form.match_type || 'all') === val}
                    onChange={() => setForm(f => ({ ...f, match_type: val }))}
                    style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                  />
                  Match <strong style={{ margin: '0 2px' }}>{label}</strong> of the conditions below
                </label>
              ))}
            </div>
            {/* Condition rows */}
            {(form.conditions || []).map((cond, idx) => (
              <div key={cond.id || idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                {/* Field selector */}
                <select
                  style={{ ...s.select, flex: '0 0 150px' }}
                  value={cond.field}
                  onChange={e => {
                    const nf = e.target.value;
                    setForm(f => ({
                      ...f,
                      conditions: f.conditions.map((c, i) =>
                        i === idx ? { ...c, field: nf, operator: slaConditionOps(nf)[0], value: '' } : c
                      ),
                    }));
                  }}
                >
                  {SLA_CONDITION_FIELDS.map(fd => <option key={fd}>{fd}</option>)}
                </select>
                {/* Operator selector */}
                <select
                  style={{ ...s.select, flex: '0 0 160px' }}
                  value={cond.operator}
                  onChange={e => setForm(f => ({
                    ...f,
                    conditions: f.conditions.map((c, i) => i === idx ? { ...c, operator: e.target.value } : c),
                  }))}
                >
                  {slaConditionOps(cond.field).map(op => <option key={op}>{op}</option>)}
                </select>
                <SlaConditionValueInput
                  field={cond.field}
                  value={cond.value}
                  options={conditionSelectValues[cond.field]}
                  onChange={val => setForm(f => ({
                    ...f,
                    conditions: f.conditions.map((c, i) => i === idx ? { ...c, value: val } : c),
                  }))}
                />
                {/* Remove */}
                <button
                  type="button"
                  title="Remove condition"
                  onClick={() => setForm(f => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }))}
                  style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: 17, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >×</button>
              </div>
            ))}
            {/* Add condition */}
            <button
              type="button"
              onClick={() => setForm(f => ({
                ...f,
                conditions: [...(f.conditions || []), { id: Date.now(), field: 'Category', operator: 'is', value: '' }],
              }))}
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 14px', border: '1.5px dashed #d1d5db', borderRadius: 6, background: 'transparent', color: '#6b7280', cursor: 'pointer', fontSize: 13 }}
            >
              <span style={{ fontSize: 18, fontWeight: 700, color: '#22c55e', lineHeight: 1 }}>+</span>
              Add condition
            </button>
          </div>

          <div style={{ margin: '20px 0 12px', fontWeight: 700, fontSize: 13 }}>SLA Targets (in {unit})</div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Priority</th>
                <th style={s.th}>First Response</th>
                <th style={s.th}>Resolution</th>
              </tr>
            </thead>
            <tbody>
              {PRIORITIES.map(p => {
                const key = p.toLowerCase();
                return (
                  <tr key={p}>
                    <td style={s.td}><PriorityBadge p={p} /></td>
                    <td style={s.td}>
                      <input type="number" min="0" step="0.5" style={{ ...s.input, width: 80 }}
                        value={form[`${key}_response_hours`] || ''} onChange={e => setForm(f => ({ ...f, [`${key}_response_hours`]: e.target.value }))} />
                    </td>
                    <td style={s.td}>
                      <input type="number" min="0" step="0.5" style={{ ...s.input, width: 80 }}
                        value={form[`${key}_resolution_hours`] || ''} onChange={e => setForm(f => ({ ...f, [`${key}_resolution_hours`]: e.target.value }))} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ ...s.row, marginTop: 20, justifyContent: 'flex-end' }}>
            <button style={s.btnGhost} onClick={() => setEditing(null)}>Cancel</button>
            <button style={s.btn()} onClick={save}>Save Policy</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
      <div style={{ ...s.row, justifyContent: 'flex-end', marginBottom: 16 }}>
        <button style={s.btn()} onClick={openNew}>+ New Policy</button>
      </div>
      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Calendar</th>
              <th style={s.th}>Unit</th>
              <th style={s.th}>Targets (Response / Resolution)</th>
              <th style={s.th}>Default</th>
              <th style={s.th}>Active</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {policies.map(p => {
              const cal = (calendars || []).find(c => c.id === p.business_calendar_id);
              return (
                <tr key={p.id}>
                  <td style={s.td}>
                    <strong>{p.name}</strong>
                    {p.description ? <div style={{ fontSize: 11, color: '#6b7280' }}>{p.description}</div> : null}
                    {p.applies_to && p.applies_to !== 'All Tickets' && (
                      <div style={{ fontSize: 11, color: '#2563eb', marginTop: 4 }}>
                        {p.applies_to}{p.applies_to_target ? `: ${p.applies_to_target}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={s.td}>{cal ? cal.name : <span style={{ color: '#9ca3af' }}>Wall clock</span>}</td>
                  <td style={s.td}>{p.time_unit || 'hours'}</td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {PRIORITIES.map(pr => (
                        <span key={pr} style={{ fontSize: 11, color: '#374151' }}>
                          <PriorityBadge p={pr} /> {p[`${pr.toLowerCase()}_response_hours`]}h / {p[`${pr.toLowerCase()}_resolution_hours`]}h
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={s.td}>{p.is_default ? <span style={s.badge('#dbeafe', '#1d4ed8')}>Yes</span> : '—'}</td>
                  <td style={s.td}><span style={s.badge(p.is_active ? '#d1fae5' : '#f3f4f6', p.is_active ? '#065f46' : '#6b7280')}>{p.is_active ? 'Active' : 'Off'}</span></td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={s.btnGhost} onClick={() => openEdit(p)}>Edit</button>
                      {!p.is_default && <button style={{ ...s.btnGhost, color: '#dc2626' }} onClick={() => del(p.id)}>Delete</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   BUSINESS CALENDARS TAB
══════════════════════════════════════════════════════════════ */
function CalendarsTab() {
  const [calendars, setCalendars] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | calendarObj
  const [hoursEditing, setHoursEditing] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [holidaysFor, setHolidaysFor] = useState(null);
  const [form, setForm] = useState({});
  const [hoursForm, setHoursForm] = useState([]);
  const [holidayForm, setHolidayForm] = useState({ holiday_name: '', holiday_date: '', is_recurring: false });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try { setCalendars(await slaCalendarsAPI.list()); } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ name: '', timezone: 'Asia/Singapore', is_24x7: false, description: '' });
    setEditing('new');
  };

  const openEdit = (cal) => {
    setForm({ ...cal });
    setEditing(cal);
  };

  const save = async () => {
    try {
      if (editing === 'new') await slaCalendarsAPI.create(form);
      else await slaCalendarsAPI.update(editing.id, form);
      setEditing(null);
      await load();
      setMsg({ type: 'success', text: 'Saved.' });
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this calendar?')) return;
    try { await slaCalendarsAPI.delete(id); await load(); } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const openHours = (cal) => {
    const defaultHours = DAYS.map((_, i) => ({
      day_of_week: i, is_working: i >= 1 && i <= 5, start_time: '08:00', end_time: '17:00'
    }));
    const merged = defaultHours.map(d => {
      const existing = (cal.working_hours || []).find(h => h.day_of_week === d.day_of_week);
      return existing ? { ...d, ...existing } : d;
    });
    setHoursForm(merged);
    setHoursEditing(cal);
  };

  const saveHours = async () => {
    try {
      await slaCalendarsAPI.saveHours(hoursEditing.id, hoursForm);
      setHoursEditing(null);
      await load();
      setMsg({ type: 'success', text: 'Working hours saved.' });
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const openHolidays = async (cal) => {
    setHolidaysFor(cal);
    try { setHolidays(await slaCalendarsAPI.getHolidays(cal.id)); } catch (_) { setHolidays([]); }
  };

  const addHoliday = async () => {
    try {
      await slaCalendarsAPI.addHoliday(holidaysFor.id, holidayForm);
      setHolidays(await slaCalendarsAPI.getHolidays(holidaysFor.id));
      setHolidayForm({ holiday_name: '', holiday_date: '', is_recurring: false });
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const deleteHoliday = async (hid) => {
    try {
      await slaCalendarsAPI.deleteHoliday(holidaysFor.id, hid);
      setHolidays(h => h.filter(x => x.id !== hid));
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  if (hoursEditing) {
    return (
      <div>
        <div style={{ ...s.row, marginBottom: 16 }}>
          <button style={s.btnGhost} onClick={() => setHoursEditing(null)}>← Back</button>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Working Hours: {hoursEditing.name}</h3>
        </div>
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Day</th>
                <th style={s.th}>Working Day</th>
                <th style={s.th}>Start Time</th>
                <th style={s.th}>End Time</th>
              </tr>
            </thead>
            <tbody>
              {hoursForm.map((h, i) => (
                <tr key={h.day_of_week}>
                  <td style={s.td}><strong>{DAYS[h.day_of_week]}</strong></td>
                  <td style={s.td}><Toggle on={h.is_working} onChange={v => setHoursForm(f => f.map((x, j) => j === i ? { ...x, is_working: v } : x))} /></td>
                  <td style={s.td}>
                    <input type="time" style={{ ...s.input, width: 110 }} disabled={!h.is_working} value={h.start_time || '08:00'}
                      onChange={e => setHoursForm(f => f.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x))} />
                  </td>
                  <td style={s.td}>
                    <input type="time" style={{ ...s.input, width: 110 }} disabled={!h.is_working} value={h.end_time || '17:00'}
                      onChange={e => setHoursForm(f => f.map((x, j) => j === i ? { ...x, end_time: e.target.value } : x))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ ...s.row, marginTop: 16, justifyContent: 'flex-end' }}>
            <button style={s.btnGhost} onClick={() => setHoursEditing(null)}>Cancel</button>
            <button style={s.btn()} onClick={saveHours}>Save Working Hours</button>
          </div>
        </div>
      </div>
    );
  }

  if (holidaysFor) {
    return (
      <div>
        <div style={{ ...s.row, marginBottom: 16 }}>
          <button style={s.btnGhost} onClick={() => setHolidaysFor(null)}>← Back</button>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Holidays: {holidaysFor.name}</h3>
        </div>
        {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
        <div style={s.card}>
          <div style={{ ...s.row, gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input style={{ ...s.input, flex: 1, minWidth: 150 }} placeholder="Holiday name" value={holidayForm.holiday_name}
              onChange={e => setHolidayForm(f => ({ ...f, holiday_name: e.target.value }))} />
            <input type="date" style={{ ...s.input, width: 150 }} value={holidayForm.holiday_date}
              onChange={e => setHolidayForm(f => ({ ...f, holiday_date: e.target.value }))} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Toggle on={holidayForm.is_recurring} onChange={v => setHolidayForm(f => ({ ...f, is_recurring: v }))} />
              <span style={{ fontSize: 12, color: '#6b7280' }}>Recurring yearly</span>
            </div>
            <button style={s.btn()} onClick={addHoliday}>Add</button>
          </div>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Name</th><th style={s.th}>Date</th><th style={s.th}>Recurring</th><th style={s.th}></th></tr></thead>
            <tbody>
              {holidays.length === 0 && <tr><td colSpan={4} style={{ ...s.td, color: '#9ca3af', textAlign: 'center' }}>No holidays added</td></tr>}
              {holidays.map(h => (
                <tr key={h.id}>
                  <td style={s.td}>{h.holiday_name}</td>
                  <td style={s.td}>{String(h.holiday_date).slice(0, 10)}</td>
                  <td style={s.td}>{h.is_recurring ? <span style={s.badge('#dbeafe','#1d4ed8')}>Yes</span> : '—'}</td>
                  <td style={s.td}><button style={{ ...s.btnGhost, color: '#dc2626' }} onClick={() => deleteHoliday(h.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div>
        <div style={{ ...s.row, marginBottom: 16 }}>
          <button style={s.btnGhost} onClick={() => setEditing(null)}>← Back</button>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{editing === 'new' ? 'New Business Calendar' : `Edit: ${form.name}`}</h3>
        </div>
        {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
        <div style={s.card}>
          <div style={s.formGrid}>
            <div style={{ ...s.formGroup, gridColumn: '1/-1' }}>
              <label style={s.label}>Calendar Name *</label>
              <input style={s.input} value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Timezone</label>
              <select style={s.select} value={form.timezone || 'Asia/Singapore'} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
                {['Asia/Singapore', 'Asia/Kuala_Lumpur', 'Asia/Jakarta', 'Asia/Bangkok', 'UTC', 'America/New_York', 'Europe/London'].map(tz => <option key={tz}>{tz}</option>)}
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>24×7 (always on)</label>
              <Toggle on={!!form.is_24x7} onChange={v => setForm(f => ({ ...f, is_24x7: v }))} />
            </div>
            <div style={{ ...s.formGroup, gridColumn: '1/-1' }}>
              <label style={s.label}>Description</label>
              <input style={s.input} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div style={{ ...s.row, marginTop: 20, justifyContent: 'flex-end' }}>
            <button style={s.btnGhost} onClick={() => setEditing(null)}>Cancel</button>
            <button style={s.btn()} onClick={save}>Save Calendar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
      <div style={{ ...s.row, justifyContent: 'flex-end', marginBottom: 16 }}>
        <button style={s.btn()} onClick={openNew}>+ New Calendar</button>
      </div>
      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Timezone</th>
              <th style={s.th}>Type</th>
              <th style={s.th}>Working Days</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {calendars.map(cal => {
              const workingDays = (cal.working_hours || []).filter(h => h.is_working).map(h => DAY_SHORT[h.day_of_week]).join(', ');
              return (
                <tr key={cal.id}>
                  <td style={s.td}><strong>{cal.name}</strong>{cal.description ? <div style={{ fontSize: 11, color: '#6b7280' }}>{cal.description}</div> : null}</td>
                  <td style={s.td}>{cal.timezone}</td>
                  <td style={s.td}>{cal.is_24x7 ? <span style={s.badge('#dbeafe', '#1d4ed8')}>24×7</span> : <span style={s.badge('#f3f4f6', '#374151')}>Business Hours</span>}</td>
                  <td style={s.td}>{cal.is_24x7 ? 'All days' : workingDays || '—'}</td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={s.btnGhost} onClick={() => openEdit(cal)}>Edit</button>
                      {!cal.is_24x7 && <button style={s.btnGhost} onClick={() => openHours(cal)}>Working Hours</button>}
                      <button style={s.btnGhost} onClick={() => openHolidays(cal)}>Holidays</button>
                      <button style={{ ...s.btnGhost, color: '#dc2626' }} onClick={() => del(cal.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ESCALATION RULES TAB
══════════════════════════════════════════════════════════════ */
function EscalationTab() {
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState('');
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState({ threshold_percent: 80, notify_role: 'manager', send_email: true, send_inapp: true, is_escalation: false, description: '' });
  const [editRow, setEditRow] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    slaAPI.list().then(ps => {
      setPolicies(ps);
      if (ps.length) setSelectedPolicy(String(ps[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedPolicy) return;
    slaEscalationAPI.list(selectedPolicy).then(setRules).catch(() => setRules([]));
  }, [selectedPolicy]);

  const save = async () => {
    try {
      const data = { ...form, sla_policy_id: Number(selectedPolicy) };
      if (editRow === 'new') await slaEscalationAPI.create(data);
      else await slaEscalationAPI.update(editRow, form);
      setEditRow(null);
      setRules(await slaEscalationAPI.list(selectedPolicy));
      setMsg({ type: 'success', text: 'Saved.' });
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const del = async (id) => {
    try {
      await slaEscalationAPI.delete(id);
      setRules(r => r.filter(x => x.id !== id));
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const threshold_color = (t) => {
    if (t >= 100) return '#dc2626';
    if (t >= 80)  return '#f59e0b';
    if (t >= 60)  return '#f97316';
    return '#10b981';
  };

  return (
    <div>
      {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
      <div style={{ ...s.row, marginBottom: 16, alignItems: 'flex-end' }}>
        <div style={s.formGroup}>
          <label style={s.label}>SLA Policy</label>
          <select style={s.select} value={selectedPolicy} onChange={e => setSelectedPolicy(e.target.value)}>
            {policies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <button style={s.btn()} onClick={() => { setForm({ threshold_percent: 80, notify_role: 'manager', send_email: true, send_inapp: true, is_escalation: false, description: '' }); setEditRow('new'); }}>+ Add Rule</button>
      </div>

      {editRow !== null && (
        <div style={{ ...s.card, background: '#f8fafc', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>{editRow === 'new' ? 'New Escalation Rule' : 'Edit Rule'}</div>
          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.label}>Threshold %</label>
              <input type="number" min="1" max="500" style={s.input} value={form.threshold_percent}
                onChange={e => setForm(f => ({ ...f, threshold_percent: Number(e.target.value) }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Notify Role</label>
              <select style={s.select} value={form.notify_role} onChange={e => setForm(f => ({ ...f, notify_role: e.target.value }))}>
                {NOTIFY_ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Notify Email (optional)</label>
              <input type="email" style={s.input} placeholder="specific@email.com" value={form.notify_email || ''}
                onChange={e => setForm(f => ({ ...f, notify_email: e.target.value }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Send Email</label>
              <Toggle on={!!form.send_email} onChange={v => setForm(f => ({ ...f, send_email: v }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Send In-App</label>
              <Toggle on={!!form.send_inapp} onChange={v => setForm(f => ({ ...f, send_inapp: v }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Hard Escalation</label>
              <Toggle on={!!form.is_escalation} onChange={v => setForm(f => ({ ...f, is_escalation: v }))} />
            </div>
            <div style={{ ...s.formGroup, gridColumn: '1/-1' }}>
              <label style={s.label}>Description</label>
              <input style={s.input} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div style={{ ...s.row, marginTop: 12, justifyContent: 'flex-end' }}>
            <button style={s.btnGhost} onClick={() => setEditRow(null)}>Cancel</button>
            <button style={s.btn()} onClick={save}>Save</button>
          </div>
        </div>
      )}

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Threshold</th>
              <th style={s.th}>Notify Role</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>In-App</th>
              <th style={s.th}>Hard Escalation</th>
              <th style={s.th}>Description</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#9ca3af' }}>No escalation rules for this policy</td></tr>}
            {[...rules].sort((a, b) => a.threshold_percent - b.threshold_percent).map(r => (
              <tr key={r.id}>
                <td style={s.td}>
                  <span style={{ ...s.badge(threshold_color(r.threshold_percent) + '22', threshold_color(r.threshold_percent)), fontWeight: 700 }}>
                    {r.threshold_percent}%
                  </span>
                </td>
                <td style={s.td}><span style={s.badge()}>{r.notify_role}</span></td>
                <td style={s.td}>{r.send_email ? '✓' : '—'}</td>
                <td style={s.td}>{r.send_inapp ? '✓' : '—'}</td>
                <td style={s.td}>{r.is_escalation ? <span style={s.badge('#fef3c7','#92400e')}>Yes</span> : '—'}</td>
                <td style={s.td}>{r.description || '—'}</td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={s.btnGhost} onClick={() => { setForm({ ...r }); setEditRow(r.id); }}>Edit</button>
                    <button style={{ ...s.btnGhost, color: '#dc2626' }} onClick={() => del(r.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PRIORITY RULES TAB
══════════════════════════════════════════════════════════════ */
const CONDITION_FIELDS = [
  { key: 'keywords', label: 'Subject contains keywords', type: 'tags' },
  { key: 'requester_role', label: 'Requester role', type: 'select', options: ROLES },
  { key: 'category', label: 'Category equals', type: 'text' },
  { key: 'vip', label: 'VIP requester', type: 'bool' },
  { key: 'department', label: 'Department equals', type: 'text' },
];

function PriorityRulesTab() {
  const [rules, setRules] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ name: '', result_priority: 'High', sort_order: 0, is_active: true, condition_json: {} });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try { setRules(await slaPriorityAPI.list()); } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const setCond = (key, val) => setForm(f => ({ ...f, condition_json: { ...f.condition_json, [key]: val } }));
  const delCond = (key) => setForm(f => {
    const c = { ...f.condition_json }; delete c[key]; return { ...f, condition_json: c };
  });

  const save = async () => {
    try {
      if (editRow === 'new') await slaPriorityAPI.create(form);
      else await slaPriorityAPI.update(editRow, form);
      setEditRow(null);
      await load();
      setMsg({ type: 'success', text: 'Saved.' });
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const del = async (id) => {
    try { await slaPriorityAPI.delete(id); setRules(r => r.filter(x => x.id !== id)); } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  return (
    <div>
      {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
      <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
        Rules are evaluated in order. First matching rule assigns the priority. Configure conditions like keywords, requester role, or department.
      </div>
      <div style={{ ...s.row, justifyContent: 'flex-end', marginBottom: 16 }}>
        <button style={s.btn()} onClick={() => { setForm({ name: '', result_priority: 'High', sort_order: rules.length, is_active: true, condition_json: {} }); setEditRow('new'); }}>+ Add Rule</button>
      </div>

      {editRow !== null && (
        <div style={{ ...s.card, background: '#f8fafc', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>{editRow === 'new' ? 'New Priority Rule' : 'Edit Rule'}</div>
          <div style={s.formGrid}>
            <div style={{ ...s.formGroup, gridColumn: '1/-1' }}>
              <label style={s.label}>Rule Name *</label>
              <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Assign Priority</label>
              <select style={s.select} value={form.result_priority} onChange={e => setForm(f => ({ ...f, result_priority: e.target.value }))}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Sort Order</label>
              <input type="number" style={s.input} value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Active</label>
              <Toggle on={!!form.is_active} onChange={v => setForm(f => ({ ...f, is_active: v }))} />
            </div>
          </div>
          <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, fontSize: 13 }}>Conditions (all must match)</div>
          {CONDITION_FIELDS.map(cf => {
            const active = cf.key in (form.condition_json || {});
            return (
              <div key={cf.key} style={{ ...s.row, background: active ? '#f0f9ff' : 'transparent', borderRadius: 6, padding: '6px 10px', border: active ? '1px solid #bae6fd' : '1px solid transparent' }}>
                <Toggle on={active} onChange={v => v ? setCond(cf.key, cf.type === 'bool' ? true : cf.type === 'tags' ? [] : '') : delCond(cf.key)} />
                <span style={{ fontSize: 13, minWidth: 180 }}>{cf.label}</span>
                {active && cf.type === 'text' && (
                  <input style={{ ...s.input, flex: 1 }} value={form.condition_json[cf.key] || ''} onChange={e => setCond(cf.key, e.target.value)} />
                )}
                {active && cf.type === 'select' && (
                  <select style={s.select} value={form.condition_json[cf.key] || ''} onChange={e => setCond(cf.key, e.target.value)}>
                    <option value="">Select…</option>
                    {cf.options.map(o => <option key={o}>{o}</option>)}
                  </select>
                )}
                {active && cf.type === 'tags' && (
                  <div style={{ flex: 1 }}>
                    <input style={s.input} placeholder="Comma-separated keywords" value={(form.condition_json[cf.key] || []).join(', ')}
                      onChange={e => setCond(cf.key, e.target.value.split(',').map(x => x.trim()).filter(Boolean))} />
                  </div>
                )}
                {active && cf.type === 'bool' && <span style={{ fontSize: 12, color: '#2563eb' }}>Enabled</span>}
              </div>
            );
          })}
          <div style={{ ...s.row, marginTop: 16, justifyContent: 'flex-end' }}>
            <button style={s.btnGhost} onClick={() => setEditRow(null)}>Cancel</button>
            <button style={s.btn()} onClick={save}>Save Rule</button>
          </div>
        </div>
      )}

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>#</th>
              <th style={s.th}>Rule Name</th>
              <th style={s.th}>Conditions</th>
              <th style={s.th}>Priority</th>
              <th style={s.th}>Active</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#9ca3af' }}>No priority rules configured</td></tr>}
            {rules.map((r, i) => {
              const cond = typeof r.condition_json === 'string' ? JSON.parse(r.condition_json) : (r.condition_json || {});
              const condSummary = Object.entries(cond).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`).join(' | ');
              return (
                <tr key={r.id}>
                  <td style={s.td}>{i + 1}</td>
                  <td style={s.td}><strong>{r.name}</strong></td>
                  <td style={s.td}><code style={{ fontSize: 11, color: '#374151' }}>{condSummary || '(any)'}</code></td>
                  <td style={s.td}><PriorityBadge p={r.result_priority} /></td>
                  <td style={s.td}><span style={s.badge(r.is_active ? '#d1fae5' : '#f3f4f6', r.is_active ? '#065f46' : '#6b7280')}>{r.is_active ? 'Active' : 'Off'}</span></td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={s.btnGhost} onClick={() => { setForm({ ...r, condition_json: typeof r.condition_json === 'string' ? JSON.parse(r.condition_json) : r.condition_json }); setEditRow(r.id); }}>Edit</button>
                      <button style={{ ...s.btnGhost, color: '#dc2626' }} onClick={() => del(r.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PAUSE RULES TAB
══════════════════════════════════════════════════════════════ */
function PauseRulesTab() {
  const [rules, setRules] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ status_value: '', pause_sla: true, require_approval: false, approver_role: 'manager', description: '', is_active: true });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try { setRules(await slaPauseAPI.list()); } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editRow === 'new') await slaPauseAPI.create(form);
      else await slaPauseAPI.update(editRow, form);
      setEditRow(null);
      await load();
      setMsg({ type: 'success', text: 'Saved.' });
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const del = async (id) => {
    try { await slaPauseAPI.delete(id); setRules(r => r.filter(x => x.id !== id)); } catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const quickToggle = async (r, field, val) => {
    try {
      await slaPauseAPI.update(r.id, { [field]: val });
      setRules(rs => rs.map(x => x.id === r.id ? { ...x, [field]: val } : x));
    } catch (_) {}
  };

  return (
    <div>
      {msg && <div style={msg.type === 'error' ? s.errorBox : s.successBox}>{msg.text}</div>}
      <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
        Define which ticket statuses should pause the SLA timer. When a ticket moves to a paused status, the SLA clock stops until it returns to an active status.
      </div>
      <div style={{ ...s.row, justifyContent: 'flex-end', marginBottom: 16 }}>
        <button style={s.btn()} onClick={() => { setForm({ status_value: '', pause_sla: true, require_approval: false, approver_role: 'manager', description: '', is_active: true }); setEditRow('new'); }}>+ Add Rule</button>
      </div>

      {editRow !== null && (
        <div style={{ ...s.card, background: '#f8fafc', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>{editRow === 'new' ? 'New Pause Rule' : 'Edit Pause Rule'}</div>
          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.label}>Status Value *</label>
              <input style={s.input} placeholder="e.g. Waiting for User" value={form.status_value}
                onChange={e => setForm(f => ({ ...f, status_value: e.target.value }))} disabled={editRow !== 'new'} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Pause SLA</label>
              <Toggle on={!!form.pause_sla} onChange={v => setForm(f => ({ ...f, pause_sla: v }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Require Approval</label>
              <Toggle on={!!form.require_approval} onChange={v => setForm(f => ({ ...f, require_approval: v }))} />
            </div>
            {form.require_approval && (
              <div style={s.formGroup}>
                <label style={s.label}>Approver Role</label>
                <select style={s.select} value={form.approver_role || 'manager'} onChange={e => setForm(f => ({ ...f, approver_role: e.target.value }))}>
                  {NOTIFY_ROLES.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            )}
            <div style={s.formGroup}>
              <label style={s.label}>Active</label>
              <Toggle on={!!form.is_active} onChange={v => setForm(f => ({ ...f, is_active: v }))} />
            </div>
            <div style={{ ...s.formGroup, gridColumn: '1/-1' }}>
              <label style={s.label}>Description</label>
              <input style={s.input} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div style={{ ...s.row, marginTop: 12, justifyContent: 'flex-end' }}>
            <button style={s.btnGhost} onClick={() => setEditRow(null)}>Cancel</button>
            <button style={s.btn()} onClick={save}>Save</button>
          </div>
        </div>
      )}

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Status Value</th>
              <th style={s.th}>Pause SLA</th>
              <th style={s.th}>Require Approval</th>
              <th style={s.th}>Approver Role</th>
              <th style={s.th}>Description</th>
              <th style={s.th}>Active</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#9ca3af' }}>No pause rules configured</td></tr>}
            {rules.map(r => (
              <tr key={r.id}>
                <td style={s.td}><strong>{r.status_value}</strong></td>
                <td style={s.td}><Toggle on={!!r.pause_sla} onChange={v => quickToggle(r, 'pause_sla', v)} /></td>
                <td style={s.td}>{r.require_approval ? <span style={s.badge('#fef3c7','#92400e')}>Required</span> : '—'}</td>
                <td style={s.td}>{r.approver_role || '—'}</td>
                <td style={s.td}>{r.description || '—'}</td>
                <td style={s.td}><Toggle on={!!r.is_active} onChange={v => quickToggle(r, 'is_active', v)} /></td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={s.btnGhost} onClick={() => { setForm({ ...r }); setEditRow(r.id); }}>Edit</button>
                    <button style={{ ...s.btnGhost, color: '#dc2626' }} onClick={() => del(r.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN SLAAdmin COMPONENT
══════════════════════════════════════════════════════════════ */
export default function SLAAdmin() {
  const [activeTab, setActiveTab] = useState('Policies');
  const [calendars, setCalendars] = useState([]);

  // Load calendars once (shared between Policies and Calendars tabs)
  useEffect(() => {
    slaCalendarsAPI.list().then(setCalendars).catch(() => {});
  }, []);

  return (
    <div style={s.root}>
      <div style={{ ...s.tabs, flexShrink: 0 }}>
        {TABS.map(t => (
          <button key={t} style={s.tab(activeTab === t)} onClick={() => setActiveTab(t)}>{t}</button>
        ))}
      </div>
      <div style={s.scroll}>
        {activeTab === 'Policies'          && <PoliciesTab calendars={calendars} />}
        {activeTab === 'Business Calendars' && <CalendarsTab />}
        {activeTab === 'Escalation Rules'   && <EscalationTab />}
        {activeTab === 'Priority Rules'     && <PriorityRulesTab />}
        {activeTab === 'Pause Rules'        && <PauseRulesTab />}
      </div>
    </div>
  );
}
